<template>
  <q-card flat class="preview-card">
    <q-card-section class="q-py-sm q-card__section q-card__section--vert">
      <div class="crud-title">
        <span>{{ this.$gettext(title) }}</span>
      </div>
    </q-card-section>
    <q-card-section class="preview-section">
      <div v-if="purposeLabel || channelLabel" class="template-chip-row q-mb-md">
        <span v-if="purposeLabel" class="template-chip template-chip--purpose">
          {{ purposeLabel }}
        </span>
        <span v-if="channelLabel" class="template-chip template-chip--channel" :style="channelChipStyle">
          {{ channelLabel }}
        </span>
      </div>

        <div class="preview-email">
          <div class="preview-subject q-mb-md">
            <div class="text-caption text-grey-6">{{ this.$gettext('SUBJECT') }}</div>
            <div class="text-subtitle1 text-weight-bold" v-html="renderedSubject"></div>
          </div>

          <div v-if="showImages && (headerImage || !isReadOnly)" class="preview-header-image q-mb-md">
            <div
              v-if="headerImage"
              class="image-preview"
              @click="onHeaderClick"
              :style="{ cursor: isReadOnly ? 'default' : 'pointer' }"
            >
              <img :src="headerImage" />
              <q-btn
                v-if="showControls"
                icon="close"
                round
                dense
                color="negative"
                size="sm"
                class="remove-btn"
                @click.stop="onRemoveHeader"
              />
            </div>
            <div
              v-else
              class="preview-image-placeholder"
              @click="onHeaderClick"
              :style="{ cursor: isReadOnly ? 'default' : 'pointer' }"
            >
              <q-icon name="image" size="28px" color="grey-5" />
              <div class="text-caption text-grey-6 q-mt-xs">{{ this.$gettext('DRAG_IMAGE_640') }}</div>
            </div>
          </div>

          <div class="preview-body q-mb-md">
            <div v-if="bodyHtml" v-html="renderedBodyHtml" class="text-body1"></div>
            <div v-else class="text-grey-6">
              <p>{{ this.$gettext('MESSAGE_CONTENT_PREVIEW') }}</p>
            </div>
            <div v-if="hasRenderedBodyContent" style="padding:16px 24px 0 0; text-align:center; border-top:0px solid #eeeeee">
              <p style="margin:0;font-size:12px;color:#888888">
                <span style="text-decoration: underline;  cursor: pointer;">{{ this.$gettext('MSG_TEMPLATE_UNSUBSCRIBE_LINK_TEXT') }}</span>
              </p>
              <q-tooltip>{{ this.$gettext('MSG_TEMPLATE_UNSUBSCRIBE_AUTO_INSERTED') }}</q-tooltip>
            </div>

          </div>

          <div v-if="showImages && (footerImage || !isReadOnly)" class="preview-footer-image">
            <div
              v-if="footerImage"
              class="image-preview"
              @click="onFooterClick"
              :style="{ cursor: isReadOnly ? 'default' : 'pointer' }"
            >
              <img :src="footerImage" />
              <q-btn
                v-if="showControls"
                icon="close"
                round
                dense
                color="negative"
                size="sm"
                class="remove-btn"
                @click.stop="onRemoveFooter"
              />
            </div>
            <div
              v-else
              class="preview-image-placeholder"
              @click="onFooterClick"
              :style="{ cursor: isReadOnly ? 'default' : 'pointer' }"
            >
              <q-icon name="image" size="28px" color="grey-5" />
              <div class="text-caption text-grey-6 q-mt-xs">{{ this.$gettext('DRAG_IMAGE_640') }}</div>
            </div>
          </div>
        </div>

    </q-card-section>
  </q-card>
</template>

<script>
import { Component, Vue, Prop } from 'vue-property-decorator'
import { getVariableColorScheme } from '../utils/variableColorScheme.js'
import { getChannelColor, getChannelIcon, getChannelLabel } from '../utils/channelMappings.js'

@Component({
  name: 'TemplatePreviewPanel'
})
class TemplatePreviewPanel extends Vue {
  @Prop({ type: String, default: 'MESSAGE_PREVIEW' }) title
  @Prop({ type: String, default: '' }) subject
  @Prop({ type: String, default: '' }) bodyHtml
  @Prop({ type: String, default: '' }) purposeLabel
  @Prop({ type: [String, Number], default: null }) channelType
  @Prop({ type: String, default: '' }) headerImage
  @Prop({ type: String, default: '' }) footerImage
  @Prop({ type: Boolean, default: false }) isReadOnly
  @Prop({ type: Boolean, default: false }) showControls
  @Prop({ type: Array, default: () => [] }) messageVariables

  get isSmsChannel () {
    // SMS channel ID is 50460002
    return String(this.channelType || '') === String(50460002) || this.channelType === 50460002
  }

  get channelLabel () {
    return this.channelType ? getChannelLabel(this.channelType) : ''
  }

  get channelIcon () {
    return this.channelType ? getChannelIcon(this.channelType) : ''
  }

  get channelColor () {
    return this.channelType ? getChannelColor(this.channelType) : ''
  }

  get showImages () {
    return !this.isSmsChannel
  }

  get renderedSubject () {
    return this.renderVariableChips(this.subject || '')
  }

  get renderedBodyHtml () {
    return this.renderVariableChips(this.bodyHtml || '')
  }

  get hasRenderedBodyContent () {
    const html = String(this.bodyHtml || '')
    const text = html
      .replace(/&nbsp;|&#160;/gi, ' ')
      .replace(/<br\s*\/?>/gi, ' ')
      .replace(/<\/p>/gi, ' ')
      .replace(/<[^>]*>/g, '')
      .replace(/\s+/g, ' ')
      .trim()
    return text.length > 0 || /\{\{\$[A-Za-z0-9_]+\}\}/.test(html)
  }

  get channelChipStyle () {
    if (!this.channelColor) return {}
    return {
      color: this.channelColor,
      borderColor: this.channelColor,
      backgroundColor: this.hexToRgba(this.channelColor, 0.08)
    }
  }

  hexToRgba (hex, alpha) {
    if (!hex || hex.length < 7) return ''
    const r = parseInt(hex.slice(1, 3), 16)
    const g = parseInt(hex.slice(3, 5), 16)
    const b = parseInt(hex.slice(5, 7), 16)
    return `rgba(${r}, ${g}, ${b}, ${alpha})`
  }

  escapeHtml (value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
  }

  getVariableByTechnicalKey (technicalKey) {
    if (!technicalKey || !this.messageVariables) {
      return null
    }

    for (const category of this.messageVariables) {
      if (!Array.isArray(category.variables)) {
        continue
      }
      const variable = category.variables.find(v => v.technicalKey === technicalKey)
      if (variable) {
        return variable
      }
    }

    return null
  }

  renderVariableChips (html) {
    const content = String(html || '')
    if (!content.includes('{{$') && !content.includes('data-chip=')) {
      return content
    }

    // Função auxiliar para detectar se é HTML
    function isHtml (str) {
      return /<[a-z][\s\S]*>/i.test(str)
    }

    // Substituir chips renderizados: procurar por spans com data-chip
    let result = content.replace(/<span[^>]*data-chip="([^"]+)"[^>]*>[\s\S]*?<\/span>/g, (_fullMatch, technicalKey) => {
      const variable = this.getVariableByTechnicalKey(technicalKey)
      if (variable?.sampleText) {
        return isHtml(variable.sampleText) ? variable.sampleText : this.escapeHtml(variable.sampleText)
      }
      return _fullMatch
    })

    // Substituir placeholders {{$KEY}}
    result = result.replace(/\{\{\$([A-Za-z0-9_]+)\}\}/g, (_fullMatch, technicalKey) => {
      const variable = this.getVariableByTechnicalKey(technicalKey)
      if (variable?.sampleText) {
        return isHtml(variable.sampleText) ? variable.sampleText : this.escapeHtml(variable.sampleText)
      }
      const label = variable?.displayLabel || variable?.displayTag || technicalKey
      const colorClass = getVariableColorScheme(variable?.variableType).colorClass
      return `<span class="editor-variable-chip ${colorClass}" data-chip="${this.escapeHtml(technicalKey)}">${this.escapeHtml(label)}</span>`
    })

    return result
  }

  onHeaderClick () {
    if (this.isReadOnly) {
      return
    }
    this.$emit('header-click')
  }

  onFooterClick () {
    if (this.isReadOnly) {
      return
    }
    this.$emit('footer-click')
  }

  onRemoveHeader () {
    this.$emit('remove-header')
  }

  onRemoveFooter () {
    this.$emit('remove-footer')
  }
}

export default TemplatePreviewPanel
</script>

<style lang="stylus" scoped>
.card-template
  border: 1px solid #e0e0e0
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05)

.preview-card
  display: flex
  flex-direction: column

.preview-section
  background-color: #fff
  border-radius: 4px
  padding: 16px

.template-chip-row
  display: flex
  align-items: center
  gap: 8px

.template-chip
  font-size: 12px
  padding: 6px 12px
  border-radius: 6px
  line-height: 1
  border: 1.5px solid
  font-weight: 500
  transition: all 0.2s ease

.template-chip--purpose
  background-color: rgba(14, 136, 228, 0.08)
  color: #0e88e4
  border: 1.5px solid #0e88e4

.template-chip--channel
  /* estilo aplicado via :style */

.email-container
  min-height: 400px
  border: 1px solid #ccc

.preview-email
  background-color: #ffffff
  border: 1px solid #e0e0e0
  border-radius: 8px
  padding: 16px

.preview-subject
  border-bottom: 1px solid #e0e0e0
  padding-bottom: 12px
  max-width: 640px
  margin: 0 auto

.preview-header-image,
.preview-footer-image
  margin-bottom: 16px

.preview-image-placeholder
  border: 2px dashed #d0d0d0
  border-radius: 6px
  padding: 20px
  text-align: center
  background-color: #fafafa
  display: flex
  flex-direction: column
  align-items: center
  justify-content: center
  min-height: 100px
  position: relative
  max-width: 500px
  margin: 0 auto

.remove-btn
  position: absolute !important
  top: 8px !important
  right: 8px !important
  z-index: 10

.image-preview
  text-align: center
  border-radius: 4px
  overflow: visible
  display: flex
  justify-content: center
  position: relative
  max-width: 500px
  margin: 0 auto

  img
    max-width: 500px
    width: 100%
    display: block

.preview-body
  line-height: 1.6
  word-wrap: break-word
  overflow-wrap: break-word
  max-width: 500px
  margin: 0 auto

  p
    margin-bottom: 16px

/* Mantem a tipografia do preview alinhada ao editor (ProseMirror) */
.preview-subject ::v-deep h1,
.preview-body ::v-deep h1
  font-size: 1.75rem
  line-height: 1.3
  font-weight: 600
  margin-top: 1rem
  margin-bottom: 0.5rem

.preview-subject ::v-deep h2,
.preview-body ::v-deep h2
  font-size: 1.5rem
  line-height: 1.3
  font-weight: 600
  margin-top: 1rem
  margin-bottom: 0.5rem

.preview-subject ::v-deep h3,
.preview-body ::v-deep h3
  font-size: 1.25rem
  line-height: 1.3
  font-weight: 600
  margin-top: 1rem
  margin-bottom: 0.5rem

.preview-subject ::v-deep h4,
.preview-subject ::v-deep h5,
.preview-subject ::v-deep h6,
.preview-body ::v-deep h4,
.preview-body ::v-deep h5,
.preview-body ::v-deep h6
  line-height: 1.3
  font-weight: 600
  margin-top: 1rem
  margin-bottom: 0.5rem

.preview-body ::v-deep p
  margin: 0.5rem 0

.preview-body ::v-deep ul,
.preview-body ::v-deep ol
  padding-left: 1.5rem
  margin: 0.5rem 0

.preview-body ::v-deep ul
  list-style-type: disc

.preview-body ::v-deep ol
  list-style-type: decimal

@media (max-width: 768px)
  .preview-subject ::v-deep h1,
  .preview-body ::v-deep h1
    font-size: 1.5rem

  .preview-subject ::v-deep h2,
  .preview-body ::v-deep h2
    font-size: 1.25rem

  .preview-subject ::v-deep h3,
  .preview-body ::v-deep h3
    font-size: 1.1rem
</style>
